/**
 * Solar Light Theme - JavaScript
 * AstraGlow-Inspired Interactions
 */

(function() {
  'use strict';

  // ============================================
  // Sticky Header on Scroll
  // ============================================
  function initStickyHeader() {
    const header = document.querySelector('[data-header]');
    if (!header) return;

    let lastScrollY = 0;
    const scrollThreshold = 50;

    function handleScroll() {
      const currentScrollY = window.scrollY;

      if (currentScrollY > scrollThreshold) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }

      lastScrollY = currentScrollY;
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
  }

  // ============================================
  // Cart Drawer / Cart Count Update
  // ============================================
  function initCart() {
    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    
    // Listen for cart updates via Shopify's cart drawer or AJAX cart
    document.addEventListener('cart:updated', function(e) {
      updateCartCount(e.detail ? e.detail.count : 0);
    });

    // Also check for form submissions on product pages
    document.querySelectorAll('product-form').forEach(function(form) {
      form.addEventListener('submit', function(e) {
        const submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn) {
          submitBtn.classList.add('is-loading');
          submitBtn.textContent = 'Adding...';
        }
      });
    });
  }

  function updateCartCount(count) {
    const elements = document.querySelectorAll('[data-cart-count]');
    elements.forEach(function(el) {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  // ============================================
  // Product Card - Quick Add to Cart
  // ============================================
  function initProductCards() {
    document.querySelectorAll('.product-card').forEach(function(card) {
      const form = card.querySelector('product-form');
      if (!form) return;

      const submitBtn = form.querySelector('[type="submit"]');
      if (!submitBtn) return;

      form.addEventListener('submit', function(e) {
        e.preventDefault();

        const originalText = submitBtn.textContent;
        submitBtn.classList.add('is-loading');
        submitBtn.disabled = true;
        submitBtn.textContent = 'Adding...';

        const formData = new FormData(form);

        fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: {
            'Content-Type': 'application/json'
          }
        })
        .then(function(response) {
          return response.json();
        })
        .then(function(data) {
          submitBtn.textContent = 'Added! ✓';
          submitBtn.classList.remove('is-loading');
          submitBtn.classList.add('is-success');

          // Update cart count
          fetch('/cart.js')
            .then(function(res) { return res.json(); })
            .then(function(cart) {
              document.dispatchEvent(new CustomEvent('cart:updated', {
                detail: { count: cart.item_count }
              }));
              updateCartCount(cart.item_count);
            });

          setTimeout(function() {
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
            submitBtn.classList.remove('is-success');
          }, 2000);
        })
        .catch(function(error) {
          submitBtn.textContent = 'Error';
          submitBtn.classList.remove('is-loading');
          submitBtn.disabled = false;
          console.error('Cart error:', error);
          setTimeout(function() {
            submitBtn.textContent = originalText;
          }, 2000);
        });
      });
    });
  }

  // ============================================
  // Lazy Load Images (Native + Intersection Observer)
  // ============================================
  function initLazyLoad() {
    if ('loading' in HTMLImageElement.prototype) {
      // Native lazy loading supported, rely on browser
      return;
    }

    const images = document.querySelectorAll('img[loading="lazy"]');
    if (!images.length) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src || img.src;
            observer.unobserve(img);
          }
        });
      }, {
        rootMargin: '50px 0px'
      });

      images.forEach(function(img) {
        observer.observe(img);
      });
    }
  }

  // ============================================
  // Smooth Scroll for Anchor Links
  // ============================================
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(function(anchor) {
      anchor.addEventListener('click', function(e) {
        const targetId = this.getAttribute('href');
        if (targetId === '#') return;

        const target = document.querySelector(targetId);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }

  // ============================================
  // Mobile Menu Toggle
  // ============================================
  function initMobileMenu() {
    const menuToggle = document.querySelector('[data-menu-toggle]');
    const mobileNav = document.querySelector('[data-mobile-nav]');
    
    if (!menuToggle || !mobileNav) return;

    menuToggle.addEventListener('click', function() {
      const isOpen = mobileNav.classList.contains('is-open');
      
      if (isOpen) {
        mobileNav.classList.remove('is-open');
        menuToggle.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
      } else {
        mobileNav.classList.add('is-open');
        menuToggle.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
      }
    });

    // Close on escape
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && mobileNav.classList.contains('is-open')) {
        mobileNav.classList.remove('is-open');
        menuToggle.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
      }
    });
  }

  // ============================================
  // Search Drawer
  // ============================================
  function initSearch() {
    const searchToggle = document.querySelector('[data-search-toggle]');
    const searchDrawer = document.querySelector('[data-search-drawer]');
    const searchInput = searchDrawer ? searchDrawer.querySelector('input[type="search"]') : null;

    if (!searchToggle || !searchDrawer) return;

    function openSearch() {
      searchDrawer.classList.add('is-open');
      searchToggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      setTimeout(function() {
        if (searchInput) searchInput.focus();
      }, 300);
    }

    function closeSearch() {
      searchDrawer.classList.remove('is-open');
      searchToggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    searchToggle.addEventListener('click', function() {
      if (searchDrawer.classList.contains('is-open')) {
        closeSearch();
      } else {
        openSearch();
      }
    });

    // Close on overlay click
    searchDrawer.addEventListener('click', function(e) {
      if (e.target === searchDrawer) closeSearch();
    });

    // Close on escape
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && searchDrawer.classList.contains('is-open')) {
        closeSearch();
      }
    });

    // Search form submit
    const searchForm = searchDrawer.querySelector('form');
    if (searchForm) {
      searchForm.addEventListener('submit', function(e) {
        e.preventDefault();
        const query = searchInput.value.trim();
        if (query) {
          window.location.href = '/search?q=' + encodeURIComponent(query);
        }
      });
    }
  }

  // ============================================
  // Product Image Gallery (Single & Zoom)
  // ============================================
  function initProductGallery() {
    const gallery = document.querySelector('[data-product-gallery]');
    if (!gallery) return;

    const mainImage = gallery.querySelector('[data-main-image]');
    const thumbnails = gallery.querySelectorAll('[data-thumbnail]');

    thumbnails.forEach(function(thumb) {
      thumb.addEventListener('click', function() {
        const newSrc = this.dataset.src || this.src;
        if (mainImage) {
          mainImage.src = newSrc;
        }
        thumbnails.forEach(function(t) { t.classList.remove('is-active'); });
        this.classList.add('is-active');
      });
    });
  }

  // ============================================
  // Tabs Component (Reviews, Product Details)
  // ============================================
  function initTabs() {
    document.querySelectorAll('[data-tabs]').forEach(function(tabsContainer) {
      const triggers = tabsContainer.querySelectorAll('[data-tab-trigger]');
      const panels = tabsContainer.querySelectorAll('[data-tab-panel]');

      triggers.forEach(function(trigger) {
        trigger.addEventListener('click', function() {
          const targetId = this.dataset.tabTrigger;

          triggers.forEach(function(t) { t.classList.remove('is-active'); });
          panels.forEach(function(p) { p.classList.remove('is-active'); });

          this.classList.add('is-active');
          const panel = document.getElementById(targetId);
          if (panel) panel.classList.add('is-active');
        });
      });
    });
  }

  // ============================================
  // Newsletter Form
  // ============================================
  function initNewsletter() {
    document.querySelectorAll('[data-newsletter-form]').forEach(function(form) {
      form.addEventListener('submit', function(e) {
        e.preventDefault();

        const input = form.querySelector('input[type="email"]');
        const submitBtn = form.querySelector('[type="submit"]');
        if (!input || !submitBtn) return;

        const email = input.value.trim();
        if (!email || !email.includes('@')) {
          input.classList.add('is-error');
          return;
        }

        input.classList.remove('is-error');
        submitBtn.disabled = true;
        submitBtn.textContent = 'Subscribing...';

        // Shopify mailing list integration
        fetch('/contact#contact_form', {
          method: 'POST',
          body: new FormData(form),
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        })
        .then(function() {
          submitBtn.textContent = 'Subscribed! ✓';
          input.value = '';
        })
        .catch(function() {
          submitBtn.textContent = 'Error, try again';
        })
        .finally(function() {
          setTimeout(function() {
            submitBtn.disabled = false;
            submitBtn.textContent = submitBtn.dataset.originalText || 'Subscribe';
          }, 3000);
        });
      });

      // Store original text
      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.dataset.originalText = submitBtn.textContent;
      }
    });
  }

  // ============================================
  // Initialize All Modules
  // ============================================
  function init() {
    initStickyHeader();
    initCart();
    initProductCards();
    initLazyLoad();
    initSmoothScroll();
    initMobileMenu();
    initSearch();
    initProductGallery();
    initTabs();
    initNewsletter();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run for Shopify theme editor
  if (typeof theme !== 'undefined' && theme.editor) {
    document.addEventListener('shopify:section:load', init);
  }

})();
